/**
 * The SMTP test form: what a valid set of settings looks like, and the one rule about the
 * password.
 *
 * ## What this is, and what it is not
 *
 * The settings themselves are provider credentials and are saved on the Provider keys page.
 * This is the surface that **proves them** before they are trusted with real mail: it opens a
 * real submission session against the settings and sends one real message, and nothing is
 * saved. That order is the point. SMTP is the one credential in the catalogue whose failure is
 * silent and delayed: a wrong API key fails the next call with a 401 somebody sees, a wrong SMTP
 * port fails at a socket timeout thirty seconds later inside a worker, on a receipt nobody is
 * watching, and the first symptom is a customer saying they never got their code, days after the
 * change that caused it.
 *
 * ## The password rule
 *
 * A stored SMTP password is never rendered back into this form. Not as dots, not as a
 * placeholder the field could resubmit. The console has no way to obtain one: it is never
 * returned by any read.
 *
 * `password` is therefore **optional in the request, and absent by default**. Omitted, the
 * server tests the stored one. That is not a convenience: an operator changing a port or an
 * encryption mode has no password to resend, and a test that demanded one would force them to
 * retype a secret they may not have, or to save first and test afterwards, which is exactly the
 * order this exists to prevent.
 *
 * `buildSmtpTestRequest` is the only thing that constructs the body, so the rule has one
 * implementation and one test.
 */
import type { SmtpTestRequest } from '../contracts/types.js';

/** Mirrors the contract's `tlsMode` enum, plus the empty string it accepts for "unset". */
export const TLS_MODES = ['starttls', 'implicit', 'none'] as const;

export type TlsMode = (typeof TLS_MODES)[number];

export const TLS_LABELS: Readonly<Record<TlsMode, string>> = {
  starttls: 'STARTTLS',
  implicit: 'TLS from the first byte',
  none: 'None (plain)',
};

export const TLS_HINTS: Readonly<Record<TlsMode, string>> = {
  starttls: 'Connects in the clear, then upgrades. The usual choice on port 587.',
  implicit: 'Encrypted before anything is sent. The usual choice on port 465.',
  none: 'No encryption at all. Only ever correct for a relay on this machine.',
};

/** The conventional port for each mode, offered as a default rather than forced. */
export const DEFAULT_PORTS: Readonly<Record<TlsMode, string>> = {
  none: '25',
  starttls: '587',
  implicit: '465',
};

/** The form's own state. Every field a string, because a half-typed port is not a number. */
export interface SmtpDraft {
  readonly host: string;
  readonly port: string;
  readonly username: string;
  readonly from: string;
  readonly fromName: string;
  readonly replyTo: string;
  readonly tlsMode: TlsMode;
  readonly to: string;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Hosts where an unencrypted hop never leaves the machine. */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

export function emptySmtpDraft(): SmtpDraft {
  return {
    host: '',
    port: DEFAULT_PORTS.starttls,
    username: '',
    from: '',
    fromName: '',
    replyTo: '',
    tlsMode: 'starttls',
    to: '',
  };
}

export type PasswordMode = 'stored' | 'typed';

/**
 * Every problem with the draft, at once.
 *
 * All of them rather than the first, for the same reason the palette checker returns every pair:
 * an operator with two fields wrong should fix two fields, not discover the second after
 * correcting the first.
 */
export function validateSmtpDraft(
  draft: SmtpDraft,
  password: { readonly mode: PasswordMode; readonly typed: string },
): readonly string[] {
  const problems: string[] = [];
  const host = draft.host.trim();

  if (host === '') {
    problems.push('Enter the SMTP host, for example smtp.postmarkapp.com.');
  } else if (/\s/.test(host) || host.includes('/')) {
    problems.push('The host is a server name, not a URL. Drop the https:// and any path.');
  }

  // The contract types the port as a string of digits and lets the adapter own what a port is.
  // The bounds are still checked here, because 70000 is a typo an operator can see.
  const port = draft.port.trim();
  const portNumber = Number(port);
  if (!/^\d{1,5}$/.test(port) || portNumber < 1 || portNumber > 65535) {
    problems.push('The port must be a whole number between 1 and 65535.');
  }

  const from = draft.from.trim();
  if (from === '') problems.push('Enter the address the mail is sent from.');
  else if (!EMAIL_RE.test(from)) problems.push('The from address is not a valid email address.');

  const replyTo = draft.replyTo.trim();
  if (replyTo !== '' && !EMAIL_RE.test(replyTo)) {
    problems.push('The reply-to address is not a valid email address. Leave it empty to use the from address.');
  }

  const to = draft.to.trim();
  if (to === '') problems.push('Enter an inbox you can open, so you can confirm the test arrived.');
  else if (!EMAIL_RE.test(to)) problems.push('The test recipient is not a valid email address.');

  if (password.mode === 'typed' && password.typed === '') {
    problems.push('Type the password to test, or switch back to testing the stored one.');
  }

  // The one rule worth refusing over: a credential handed to a remote server with no encryption
  // is a credential on the wire, and this is a licensed money business. A relay on this machine
  // is exempt because there is no wire.
  const usesCredential = draft.username.trim() !== '' || password.mode === 'typed';
  if (draft.tlsMode === 'none' && usesCredential && !LOCAL_HOSTS.has(host.toLowerCase())) {
    problems.push(
      'A username and password cannot be sent to a remote server with no encryption. Choose STARTTLS or TLS.',
    );
  }

  return problems;
}

/**
 * The request body.
 *
 * The single place a password can enter a payload. `password` is present only when the operator
 * typed one; absent means "test the stored one", and an empty string is never used to mean
 * either, because to the contract an empty string means "no password at all" (an open relay).
 */
export function buildSmtpTestRequest(
  draft: SmtpDraft,
  password: { readonly mode: PasswordMode; readonly typed: string },
): SmtpTestRequest {
  const replyTo = draft.replyTo.trim();
  const base = {
    host: draft.host.trim(),
    port: draft.port.trim(),
    username: draft.username.trim(),
    from: draft.from.trim(),
    fromName: draft.fromName.trim(),
    replyTo,
    tlsMode: draft.tlsMode,
    to: draft.to.trim(),
  } satisfies Omit<SmtpTestRequest, 'password'>;

  if (password.mode === 'typed' && password.typed !== '') {
    return { ...base, password: password.typed };
  }
  return base;
}

/** What the panel says about the credential, without ever showing one. */
export function passwordStateLabel(mode: PasswordMode): string {
  return mode === 'typed'
    ? 'The password you type is used for this one test and is never saved.'
    : 'The stored password is used. It is never shown here, in any form.';
}
